import { ref } from 'vue';

import { crossbeamApi } from '@/api';
import { SHARING_TYPES } from '@/constants/data_shares';
import { captureException } from '@/errors';
import {
  useDataSharePresetsStore,
  useDataSharesStore,
  useFlashesStore,
} from '@/stores';

import type { ChangeTrackingState } from './useSharingHubChangeTracking';
import type { PartnerSharingData } from './useSharingSettingsHub';

type ChangedItem = ChangeTrackingState<number, PartnerSharingData>;

/**
 * Composable for managing save operations in population partner sharing
 */
export function usePopulationPartnerSave() {
  const dataSharesStore = useDataSharesStore();
  const dataSharePresetsStore = useDataSharePresetsStore();
  const { addUnhandledError, addSuccessFlash } = useFlashesStore();

  const isSaving = ref(false);

  /**
   * Delete partner-specific overrides (restore to population defaults)
   */
  async function deletePartnerOverrides(
    populationId: number,
    partnerIds: Set<number>,
  ): Promise<boolean> {
    for (const partnerId of partnerIds) {
      const { error } = await crossbeamApi.DELETE(
        '/v0.1/outgoing-data-shares',
        {
          params: {
            query: {
              population_id: populationId,
              partner_org_id: String(partnerId),
            },
          },
        },
      );

      if (error) {
        captureException(error);
        addUnhandledError(error as Error);
        return false;
      }
    }
    return true;
  }

  /**
   * Save partner sharing changes (creates or updates partner-population rules)
   */
  async function savePartnerChanges(
    populationId: number,
    changedItems: ChangedItem[],
  ): Promise<boolean> {
    const dataShares = changedItems
      .filter((item) => item.current.visibility)
      .map((item) => {
        const visibility = item.current.visibility as
          | typeof SHARING_TYPES.HIDDEN
          | typeof SHARING_TYPES.OLAPS_ONLY
          | typeof SHARING_TYPES.SHARING
          | typeof SHARING_TYPES.GREENFIELD_SHARING;

        const presetId =
          visibility === SHARING_TYPES.HIDDEN ||
          visibility === SHARING_TYPES.OLAPS_ONLY
            ? undefined
            : (item.current.presetId ?? undefined);

        return {
          partner_organization_id: item.id,
          visibility,
          preset_id: presetId,
        };
      });

    const payload = {
      data_shares: dataShares,
    };

    const { error } = await crossbeamApi.POST(
      '/v0.1/data-share-assignments/populations/{population_id}',
      {
        params: {
          path: {
            population_id: populationId,
          },
        },
        body: payload,
      },
    );

    if (error) {
      captureException(error);
      addUnhandledError(error as Error);
      return false;
    }

    return true;
  }

  /**
   * Main save function that handles both restores and regular changes
   */
  async function save(
    populationId: number,
    allChangedItems: ChangedItem[],
    partnerIdsToRestore: Set<number>,
    partnerIdsWithClearedOverrides?: Set<number>,
  ): Promise<boolean> {
    // Combine both sets: partners to restore AND partners with cleared overrides
    // Both need their overrides deleted before applying new changes
    const allPartnerIdsToDelete = new Set([
      ...partnerIdsToRestore,
      ...(partnerIdsWithClearedOverrides ?? []),
    ]);

    // Filter out items that will have their overrides deleted
    // (they're being restored to defaults with no new changes)
    const changedItems = allChangedItems.filter(
      (item) => !partnerIdsToRestore.has(item.id as number),
    );

    const hasDeletes = allPartnerIdsToDelete.size > 0;

    if (changedItems.length === 0 && !hasDeletes) return true;

    isSaving.value = true;

    if (hasDeletes) {
      const success = await deletePartnerOverrides(
        populationId,
        allPartnerIdsToDelete,
      );
      if (!success) {
        isSaving.value = false;
        return false;
      }
    }

    if (changedItems.length > 0) {
      const success = await savePartnerChanges(populationId, changedItems);
      if (!success) {
        isSaving.value = false;
        return false;
      }
    }

    await Promise.all([
      dataSharesStore.refreshDataSharesStore(),
      dataSharePresetsStore.refreshDataSharePresetsStore(),
    ]);

    addSuccessFlash({ message: 'Settings saved' });
    isSaving.value = false;
    return true;
  }

  return {
    isSaving,
    save,
    deletePartnerOverrides,
    savePartnerChanges,
  };
}
